import React, { useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { getApiOrigin } from '../config/environment';

const PhpInfoPage: React.FC = () => {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['phpinfo'],
    queryFn: () => apiService.getPhpInfoHtml(),
    staleTime: 0,
    gcTime: 0,
  });

  const openInNewTab = useCallback(() => {
    if (!data) {
      return;
    }
    const blob = new Blob([data], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const tab = window.open(url, '_blank', 'noopener,noreferrer');
    if (tab) {
      tab.onload = () => URL.revokeObjectURL(url);
    }
  }, [data]);

  const errMsg = React.useMemo(() => {
    if (!error) {
      return null;
    }
    const ax = error as { response?: { status?: number; data?: { error?: string; message?: string } } };
    if (ax.response?.status === 403) {
      return 'Accès refusé : compte super administrateur requis.';
    }
    return ax.response?.data?.error || ax.response?.data?.message || 'Impossible de charger phpinfo.';
  }, [error]);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" gutterBottom>
            PHP Info
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configuration PHP du serveur API ({getApiOrigin()}). Accès réservé aux super administrateurs.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => void refetch()}
            disabled={isLoading || isFetching}
          >
            Actualiser
          </Button>
          <Button variant="outlined" startIcon={<OpenInNewIcon />} onClick={openInNewTab} disabled={!data}>
            Nouvel onglet
          </Button>
        </Stack>
      </Stack>

      {errMsg && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errMsg}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {isLoading || isFetching ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : data ? (
            <Box
              component="iframe"
              title="PHP Info"
              srcDoc={data}
              sx={{
                width: '100%',
                minHeight: '75vh',
                border: 0,
                display: 'block',
                bgcolor: '#fff',
              }}
            />
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">Aucune donnée disponible.</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PhpInfoPage;
